import React, { Fragment, ReactNode } from 'react';
import Chip from '@/components/ui/Chip.tsx';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/Tooltip.tsx';
import { cn } from '@/lib/utils.ts';

/**
 * Trzeci chip w wierszu już nikomu nic nie mówi, a rozpycha kolumnę —
 * dwa widoczne, reszta pod `+N`.
 */
const MAX_VISIBLE_CHIPS = 2;

export interface CellChipItem {
  key: string;
  label: string;
  /** Gotowy chip, gdy komórka ma własną barwę — tagi dostają kolor z backendu. */
  chip?: ReactNode;
}

interface CellChipListProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> {
  items: CellChipItem[];
}

/** Lista chipów w komórce: dwa widoczne, reszta jako `+N` z tooltipem. */
export const CellChipList = ({
  items,
  className,
  ...props
}: CellChipListProps) => {
  const visible = items.slice(0, MAX_VISIBLE_CHIPS);
  const hidden = items.slice(MAX_VISIBLE_CHIPS);

  return (
    <div
      className={cn(
        'flex w-full items-center gap-1 overflow-hidden',
        className,
      )}
      {...props}
    >
      {visible.map((item) => (
        <Fragment key={item.key}>
          {item.chip ?? (
            <Chip size="sm" label={item.label} className="shrink-0" />
          )}
        </Fragment>
      ))}
      {hidden.length > 0 && (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="shrink-0 text-xs text-neutral-500">
              +{hidden.length}
            </span>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs">
            {hidden.map((item) => item.label).join(', ')}
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  );
};
